import { Button } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';

import allAggregateFunctions from '../../../utils/named-aggregate-functions';
import { generateMarks } from '../../../utils/generateMarks';
import { ColourPicker, Datability, Select, Slider, Text } from '../../../WidgetConfiguratorComponents/FormGroups';
import FlipDirection from '../../../utils/FlipDirection';
import updateHeading from './utils/updateHeading';

const AGGREGATE_FUNCTIONS_KEYS = Object.keys(allAggregateFunctions);
const SLIDER_MARKS = generateMarks([0, 1, 2, 3, 4]);
const DATA_TYPE = ['scalar', 'vector'];

const DatabilityItem = ({ heading, headingIndex, databilityId, databilityIndex, viewblockState, setViewblockState, onDelete }) => {
    const defaultDecimalPoint = viewblockState.settings.dp || 2;
    const datability = useMemo(() => viewblockState.databilities.find(({ id }) => id === databilityId), [viewblockState.databilities, databilityId]);

    const onHeadingChange = useCallback(updateHeading(headingIndex, databilityIndex, { defaultDecimalPoint }), [
        headingIndex,
        databilityIndex,
        defaultDecimalPoint,
    ]);

    const onInputChange = useCallback(
        (key) => (value) => {
            setViewblockState((prev) => onHeadingChange(prev, key, value));
        },
        [onHeadingChange, setViewblockState]
    );

    // This function allows you to update multiple properties in a heading with one setViewblockstate call.
    // Changes is {property: string, value: any}[]
    // Where property is the key (eg 'unit') and value the value (eg 'm')
    const updateMultipleHeadings = ({ headingId, databilityIndex, changes }) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                headings: prev.settings.headings.map((heading) => {
                    if (heading.id !== headingId) return heading;

                    return {
                        ...heading,
                        ...Object.fromEntries(
                            Object.keys(heading).map((key) => {
                                const change = changes.find((c) => c.property === key);

                                if (!change) return [key, heading[key]];

                                return [
                                    key,
                                    heading[key].map((value, valueIndex) => {
                                        if (valueIndex !== databilityIndex) return value;
                                        return change.value;
                                    }),
                                ];
                            })
                        ),
                    };
                }),
            },
        }));
    };

    const onChangeDatabilityHandler = useCallback(
        (datability) => {
            setViewblockState((prev) => {
                const newState = {
                    ...prev,
                    databilities: [...prev.databilities.filter(({ id }) => id !== datability.id), datability],
                };

                return onHeadingChange(newState, 'databilities', databilityId);
            });
        },
        [onHeadingChange]
    );

    const onInterpolationChangeHandler = useCallback(
        (interpolationInterval) => {
            setViewblockState((prev) => ({
                ...prev,
                databilities: prev.databilities.map((datability) =>
                    datability.id === databilityId
                        ? {
                              ...datability,
                              interpolationInterval,
                          }
                        : datability
                ),
            }));
        },
        [setViewblockState, databilityId]
    );

    const onUnitChangeHandler = useCallback(
        ({ unit }) => {
            updateMultipleHeadings({
                headingId: heading.id,
                databilityIndex: databilityIndex,
                changes: [
                    {
                        property: 'databilityUnits',
                        value: unit,
                    },
                    {
                        property: 'databilityUnitLabels',
                        value: unit,
                    },
                ],
            });
        },
        [heading.id, databilityIndex]
    );

    const onUnitLabelChangeHandler = useCallback(
        (unitLabel) => {
            updateMultipleHeadings({
                headingId: heading.id,
                databilityIndex: databilityIndex,
                changes: [
                    {
                        property: 'databilityUnitLabels',
                        value: unitLabel,
                    },
                ],
            });
        },
        [heading.id, databilityIndex]
    );

    const onDirectionChange = useCallback(
        (direction) => {
            updateMultipleHeadings({
                headingId: heading.id,
                databilityIndex: databilityIndex,
                changes: [
                    {
                        property: 'direction',
                        value: direction,
                    },
                ],
            });
        },
        [heading.id, databilityIndex]
    );

    const isDirectionalCapability = ['deg', 'rad'].includes(heading.databilityUnits[databilityIndex]);

    return (
        <>
            <Datability
                onChangeDatability={onChangeDatabilityHandler}
                unit={heading.databilityUnits[databilityIndex]}
                unitLabel={heading.databilityUnitLabels && heading.databilityUnitLabels[databilityIndex] && heading.databilityUnitLabels[databilityIndex]}
                onUnitChange={onUnitChangeHandler}
                onUnitLabelChange={onUnitLabelChangeHandler}
                dataType={DATA_TYPE}
                datability={datability}
                vectorNormalization={heading.vectorNormalization[databilityIndex]}
                onVectorNormalizationChange={onInputChange('vectorNormalization')}
                onInterpolationChange={onInterpolationChangeHandler}
            />
            {isDirectionalCapability && (
                <Select
                    label='Display directional data as'
                    value={heading.directionalVisualization[databilityIndex]}
                    values={[
                        { value: 'original', label: 'Respect selected unit' },
                        { value: 'cardinal', label: 'Cardinal direction' },
                        { value: 'arrow', label: 'Arrow icon' },
                    ]}
                    onChange={onInputChange('directionalVisualization')}
                />
            )}
            {isDirectionalCapability && (
                <FlipDirection
                    datability={datability}
                    disabled={heading.directionalVisualization[databilityIndex] !== 'arrow'}
                    onUpdateDirection={onInputChange('directions')}
                    direction={heading.directions?.[databilityIndex]}
                />
            )}
            <Text label='Label' value={heading.databilityLabels[databilityIndex]} onChange={onInputChange('databilityLabels')} />
            <ColourPicker label='Colour' value={heading.colours[databilityIndex]} onChange={onInputChange('colours')} />
            <Slider
                label='Decimal Point (if any)'
                onChange={onInputChange('dp')}
                value={heading.dp ? heading.dp[databilityIndex] : defaultDecimalPoint}
                min={0}
                max={4}
                marks={SLIDER_MARKS}
            />
            <Select
                label='Aggregation'
                value={heading.aggregateFunctions[databilityIndex]}
                values={AGGREGATE_FUNCTIONS_KEYS}
                onChange={onInputChange('aggregateFunctions')}
            />
            <div style={{ gridColumn: '1 / 6', textAlign: 'right' }}>
                <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
                    Delete&nbsp;<i>{heading.databilityLabels[databilityIndex]}</i>
                </Button>
            </div>
        </>
    );
};

export default React.memo(DatabilityItem);
